import { DateTime } from "luxon";

import type {
  IntegrationSetting,
  IntegrationSettingsKind,
} from "./integrationSettingsTypes";
import type {
  IntegrationProviderCategory,
  IntegrationProviderId,
  IntegrationProviderKind,
  ProductivityProviderId,
} from "./providerTypes";
import type { ApiProductivityChannelProvider } from "./topicTypes";

type IntegrationSyncMessageKind = "topic" | "session" | "entity";

export type IntegrationScope = "user" | "workspace";

export type IntegrationAlertKind =
  | "error"
  | "microsoft_admin_consent_required"
  | "microsoft_admin_consent_granted_reauth_required"
  | "reauth_required";

export type IntegrationConversationKind =
  | "public_channel"
  | "private_channel"
  | "mpim"
  | "im";

export interface IntegrationAlert {
  kind: IntegrationAlertKind;
  message: string;
}

/** API Types */
export interface ApiIntegration {
  kind: IntegrationProviderId;
  connected: boolean;
  scope: IntegrationScope;
  alerts: IntegrationAlert[];
}

export interface ApiSyncAllIntegrationRequestBody {
  messageKind: IntegrationSyncMessageKind;
  integrationKind: IntegrationSettingsKind;
}

type HubSpotSearchResultKind = "contacts" | "companies" | "deals";

type SalesforceSearchResultKind =
  | "Contact"
  | "Account"
  | "Lead"
  | "Opportunity";

type JiraSearchResultKind = "project" | "issueType";
type ConfluenceSearchResultKind = "space" | "page";
type NotionSearchResultKind = "page";

interface JiraSearchFields {
  fieldId: JiraSearchResultKind | string;
  projectId?: string;
  issueTypeId?: string;
}

interface ConfluenceSearchFields {
  kind: ConfluenceSearchResultKind;
  spaceKey?: string;
}

interface NotionSearchFields {
  kind: NotionSearchResultKind;
}

export interface ApiSearchIntegrationRequestBody {
  searchTerm: string;
  integrationKind: IntegrationSettingsKind;
  filters: string[];
  jiraSearch?: JiraSearchFields;
  confluenceSearch?: ConfluenceSearchFields;
  notionSearch?: NotionSearchFields;
}

export interface ApiListIntegrationFieldsRequestBody {
  integration: IntegrationSettingsKind;
  atlassianProjectId: string;
  atlassianIssueId: string;
}

/**
 * Jira docs specify this as `undefined`, so these types are simply inferred
 * from the response we get from our Jira org. There are more fields but these
 * seem to be the only ones we'll need to care about.
 * https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issues/#api-rest-api-3-issue-createmeta-projectidorkey-issuetypes-issuetypeid-get
 * */
export interface ApiJiraFieldAllowedValue {
  iconUrl?: string;
  id: string;
  name: string;
  value?: string;
}

/**
 * BE passes us Jira's response schema directly:
 * https://developer.atlassian.com/cloud/jira/platform/rest/v3/api-group-issues/#api-rest-api-3-issue-createmeta-projectidorkey-issuetypes-issuetypeid-get
 * */
export interface ApiJiraField {
  required: boolean;
  allowedValues: ApiJiraFieldAllowedValue[];
  schema: {
    custom?: string;
    /**
     * known options: array, string, number, date, datetime, any
     * as well as all of jira's internal types (e.g. user)
     * */
    type: string;
  };
  name: string;
  /** The name of the key that is passed to the jiraIssueBody when creating an issue */
  key: string;
  autoCompleteUrl?: string;
  hasDefaultValue?: boolean;
  defaultValue?: ApiJiraFieldAllowedValue;
  operations: string[]; // known options: set, add, remove, copy
  fieldId: string;
}

export type JiraSearchResultOrAllowedFieldValue =
  | IntegrationSearchResult
  | ApiJiraFieldAllowedValue;

export type JiraIssueBodyValue =
  | string
  | number
  | DateTime
  | JiraSearchResultOrAllowedFieldValue
  | JiraSearchResultOrAllowedFieldValue[];

export type JiraIssueBody = {
  [key: string]: JiraIssueBodyValue | null;
};

export type IntegrationSearchErrorReason =
  | "no_issue_meta_for_project"
  | "no_specific_issue_meta_for_project"
  | "missing_field_for_issue_type"
  | "unknown_search_error";

export type CrmIntegrationSearchResultKind =
  | HubSpotSearchResultKind
  | SalesforceSearchResultKind;

export type IntegrationSearchResultKind =
  | CrmIntegrationSearchResultKind
  | JiraSearchResultKind
  | ConfluenceSearchResultKind
  | NotionSearchResultKind;

export interface IntegrationSearchResult {
  kind: IntegrationSearchResultKind;
  id: string;
  name: string;
  notionParentId: string | null;
  organizationName: null | string;
  organizationLeadName: null | string;
  closeDate: null | string;
  isLead: null | string;
  confluenceParentPageId: string | null;
  confluenceSpaceKey: string | null;
}

export type ApiJiraIssueBodyValue =
  | string
  | number
  | string[]
  | { id: string }
  | { id: string }[];

export type ApiJiraIssueBody = {
  [key: string]: ApiJiraIssueBodyValue;
};

export interface ApiSyncIntegrationRequestBody
  extends ApiSyncAllIntegrationRequestBody {
  intervalId: string;
  oneTimeRecipients?: {
    kind: IntegrationSearchResultKind;
    id: string;
  }[];
  jiraIssueBody?: { fields: ApiJiraIssueBody };
  confluencePushParams?: {
    spaceId: string;
    parentPageId: string;
    pageTitle: string;
    includeTranscript: boolean;
    overwrite: boolean;
  };
  notionPushParams?: {
    parentPageId?: string;
    databaseId?: string;
    pageTitle: string;
    includeTranscript: boolean;
  };
}

export interface ApiSyncIntegrationResponse {
  name: string;
  link: string;
}

export interface ApiProductivityIntegrationStatus {
  source: ApiProductivityChannelProvider;
  firstRunSucceeded?: boolean;
  latestLastSyncTime?: string;
  channelCount?: number;
  messageCount?: number;
  actionItemCount?: number;
  keyQuestionCount?: number;
  topicReportIds?: string[];
  /* Date at which the user's access to this provider's channel(s) was granted.
  Should correspond to when the user connected this provider */
  userAccessCreation?: string;
}

/** Parsed/FE-only types */
export interface Integration {
  providerId: IntegrationProviderId;
  kind: IntegrationProviderKind;
  alerts: IntegrationAlert[];
  category: IntegrationProviderCategory;
  displayName: string;
  displayNameShort: string;
  documentationUrl?: string;
  demoUrl?: string;
  icon: string;
  isConnected: boolean;
  isDisabled: boolean;
  isInactive: boolean;
  isEnterpriseGPTProvider: boolean;
  isPremium: boolean;
  integrationSettings: {
    byId: { [key in IntegrationSettingsKind]?: IntegrationSetting };
    all: IntegrationSetting[];
  };
  limitedData: boolean;
  providerEmail?: string;
  route: string;
  reauthRequired: boolean;
  scope: IntegrationScope;
}

export interface IntegrationConversation {
  id: string;
  name: string;
  kind: IntegrationConversationKind;
}

export enum ProductivityIntegrationStatusStep {
  INITIAL = 1,
  INGESTION = 2,
  REPORT_GENERATION = 3,
  COMPLETE = 4,
}

export interface ProductivityIntegrationStatus {
  provider: ProductivityProviderId;
  lastSyncTime?: DateTime;
  numChannels: number | null;
  numMessages: number | null;
  numActionItems: number | null;
  numKeyQuestions: number | null;
  topicReportIds: string[];
  createdOn?: DateTime;
  step: ProductivityIntegrationStatusStep;
  error: boolean;
}
